/**
 * theme.js — Design system EkynAir
 * Source unique pour couleurs, typographie, boutons, espacements
 *
 * RÈGLE : tout composant qui a besoin d'un style visuel importe getTheme() ou TOKEN.*
 * Ne jamais hardcoder #1B77F1 ou fontSize directement dans un composant.
 */

// ─── Palette de marque ────────────────────────────────────────────────────
/**
 * Tokens couleur EkynAir.
 *
 * USAGES STRICTS :
 * - blue (#1B77F1) → icônes, bordures, focus, fills SVG, fonds avec
 *   texte blanc dessus. Ratio 4.20:1 sur blanc — FAIL AA texte normal,
 *   PASS AA non-texte (3:1) et AA Large (≥18px ou ≥14px bold).
 * - blueText (#1565C0) → COULEUR PAR DÉFAUT pour color: sur fond clair.
 *   Ratio 4.69:1 — PASS AA strict.
 * - blueEnd (#00CCFF) → ❌ INTERDIT en color/stroke/fill sur fond clair.
 *   Ratio 1.90:1 sur blanc. Réservé aux gradients décoratifs.
 * - gradient → gradient de marque (variant décoratif large). NE PAS
 *   UTILISER comme arrière-plan de texte blanc en body size — le bout
 *   #00CCFF descend à 1.90:1 et FAIL AA. Utiliser `gradientHeader` pour
 *   tout header/bandeau qui porte du texte blanc.
 * - gradientHeader (sprint UI Pro Vét V1.0, D53) → variante AA-safe :
 *   palette resserrée (#1B77F1 → #0098C5) + overlay sombre intégré.
 *   Conserve l'identité bleu→cyan tout en garantissant ≥ 4.5:1 sur
 *   texte blanc de corps. Mode dark inchangé (déjà conforme).
 */
export const BRAND = {
  blue:     '#1B77F1',
  blueText: '#1565C0',  // Texte/lien/label sur fond clair (4.69:1 AA)
  blueEnd:  '#00CCFF',
  gradient: 'linear-gradient(135deg, #1B77F1 0%, #00CCFF 100%)',
  gradientHeader: 'linear-gradient(135deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.26) 100%), linear-gradient(135deg, #1B77F1 0%, #0098C5 100%)',
  gradientHeaderDark: 'linear-gradient(135deg, #0D2A5C 0%, #0A4A8A 100%)',
  green:    '#10B981',
  red:      '#EF4444',
  orange:   '#F59E0B',
};

// ─── Tokens typographiques ────────────────────────────────────────────────
export const TYPE = {
  fontTitle: "'Poppins', sans-serif",
  fontBody:  "'Inter', sans-serif",

  // Tailles normalisées
  h1:    { fontSize: '24px', fontWeight: '700', fontFamily: "'Poppins', sans-serif" },
  h2:    { fontSize: '20px', fontWeight: '700', fontFamily: "'Poppins', sans-serif" },
  h3:    { fontSize: '16px', fontWeight: '600', fontFamily: "'Poppins', sans-serif" },
  body:  { fontSize: '14px', fontWeight: '400', fontFamily: "'Inter', sans-serif" },
  small: { fontSize: '12px', fontWeight: '400', fontFamily: "'Inter', sans-serif" },
  label: { fontSize: '13px', fontWeight: '500', fontFamily: "'Inter', sans-serif" },
  badge: { fontSize: '11px', fontWeight: '600', fontFamily: "'Inter', sans-serif" },
};

// ─── Tokens boutons (variantes) ───────────────────────────────────────────
// Usage : style={BTN.primary} ou style={BTN.secondary(darkMode)}
export const BTN = {
  /** Bouton principal — gradient bleu EkynAir */
  primary: {
    padding: '10px 20px',
    background: BRAND.gradient,
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: "'Inter', sans-serif",
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
  },
  /** Bouton secondaire — outline bleu */
  secondary: (darkMode = false) => ({
    padding: '9px 18px',
    background: 'none',
    color: darkMode ? '#90CAF9' : BRAND.blueText,
    border: `1.5px solid ${darkMode ? '#90CAF9' : BRAND.blue}`,
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    fontFamily: "'Inter', sans-serif",
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
  }),
  /** Bouton ghost — discret, actions tertiaires */
  ghost: (darkMode = false) => ({
    padding: '9px 14px',
    background: 'none',
    color: darkMode ? '#B8BFC7' : '#6B7280',
    border: `1px solid ${darkMode ? 'rgba(255,255,255,0.15)' : '#D1D5DB'}`,
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    fontFamily: "'Inter', sans-serif",
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
  }),
  /** Bouton danger — rouge, actions destructives */
  danger: {
    padding: '9px 16px',
    background: 'none',
    color: BRAND.red,
    border: `1.5px solid ${BRAND.red}`,
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    fontFamily: "'Inter', sans-serif",
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
  },
  /** Bouton succès — vert, confirmations */
  success: {
    padding: '10px 20px',
    background: BRAND.green,
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: "'Inter', sans-serif",
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
  },
};

// ─── Tokens layout ────────────────────────────────────────────────────────
export const RADIUS = {
  sm: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  pill: '999px',
};

export const SHADOW = {
  card: '0 2px 8px rgba(0,0,0,0.08)',
  elevated: '0 4px 16px rgba(0,0,0,0.12)',
  blue: '0 2px 8px rgba(27,119,241,0.2)',
};

// ─── Thème clair / sombre (inchangé) ─────────────────────────────────────
const light = {
  bg: '#F5F5F5', surface: '#FFFFFF', surfaceElevated: '#FFFFFF',
  textPrimary: '#2C3E50', textSecondary: '#4A5568', textDisabled: '#E8EDF2',
  accent: BRAND.blue, accentSecondary: BRAND.blueEnd,
  border: '#E0E0E0', borderLight: '#F0F0F0',
  card: '#FFFFFF', cardHover: '#F8F9FA',
  inputBg: '#FFFFFF', inputBorder: '#D0D0D0',
  success: BRAND.green, warning: BRAND.orange, danger: BRAND.red, info: '#3B82F6',
  shadow: SHADOW.card, shadowElevated: SHADOW.elevated,
};

const dark = {
  bg: '#1E1E32', surface: '#282845', surfaceElevated: '#2D2D42',
  textPrimary: '#E1E1E1', textSecondary: '#E8EDF2', textDisabled: '#666666',
  accent: '#90CAF9', accentSecondary: '#80DEEA',
  border: 'rgba(255,255,255,0.12)', borderLight: 'rgba(255,255,255,0.06)',
  card: '#282845', cardHover: '#2D2D42',
  inputBg: '#282845', inputBorder: 'rgba(255,255,255,0.2)',
  success: '#4ADE80', warning: '#FBBF24', danger: '#F87171', info: '#60A5FA',
  shadow: '0 2px 8px rgba(0,0,0,0.3)', shadowElevated: '0 4px 16px rgba(0,0,0,0.4)',
};

export function getTheme(darkMode) { return darkMode ? dark : light; }
export { light, dark };
